/* eslint-disable react-hooks/exhaustive-deps */
import TextField from '@material-ui/core/TextField';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import OaLabel from '@orientaction/commons/OaLabel';
import { AdminTestContext } from '@orientaction/Context';
import classNames from 'classnames';
import { forwardRef, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import {
  DIAGRAM_UNIVERS_OPTIONS,
  DiagramUniversOption,
  getDiagramUniversOption,
} from '../../../../../../pages/Redesign/shared/utils/diagramUnivers';
import { restitutionTypes } from './constant';
import style from './style';

const CustomDropdownIndicator = ({ innerRef, innerProps }: any) => (
  <ArrowDropDownIcon innerRef={innerRef} {...innerProps} />
);

const formatUniversOptionLabel = (option: DiagramUniversOption) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: option.swatchColor,
        flexShrink: 0,
      }}
    />
    <span>{option.label}</span>
  </div>
);

const customStyles = (hasError: boolean) => ({
  control: (base: any) => ({
    ...base,
    minHeight: '56px',
    border: hasError ? '1px solid #f44336' : '1px solid #e0e0e0',
    boxShadow: 'none',
    '&:hover': {
      border: hasError ? '1px solid #f44336' : '1px solid #e0e0e0',
    },
  }),
  menu: (base: any) => ({
    ...base,
  }),
});

const TitlePart = forwardRef((_, ref) => {
  const { test, updateTest } = useContext<any>(AdminTestContext);

  // Sauvegarde temporaire des variables lors du changement de type de diagramme
  const questionsVariablesBackupRef = useRef<any[]>([]);

  const classes = style();
  const styles = customStyles(false);

  const handleChange = (e: any) => {
    updateTest({ test: { ...test.test, title: e.target.value } });
  };

  const getGroup = (diagramId: number) => (diagramId === 4 ? 'threshold' : 'normal');

  const handleUniversChange = (value: DiagramUniversOption | null) => {
    updateTest({ test: { ...test.test, univers: value?.value ?? null } });
  };

  const handleSelectChange = (value: any) => {
    const currentDiagramId = test.test.diagram.id;
    const nextDiagramId = value.id;

    const currentGroup = getGroup(currentDiagramId);
    const nextGroup = getGroup(nextDiagramId);

    if (currentGroup !== nextGroup) {
      // Sauvegarde des variables si on passe de normal à seuil
      if (currentGroup === 'normal') {
        const variablesToSave = test.questions.map((q: any) => ({
          questionId: q.id,
          responses: q.responses.map((r: any) => ({
            responseId: r.id,
            variable: r.variable,
          })),
        }));
        questionsVariablesBackupRef.current = variablesToSave;
      }

      // Préparation des questions pour le nouveau type
      let updatedQuestions = test.questions.map((q: any) => ({
        ...q,
        responses: q.responses.map((r: any) => ({
          ...r,
          variable: undefined, // On retire toujours les variables en seuil
        })),
      }));

      // Si on revient en normal, on restaure les variables
      if (nextGroup === 'normal') {
        const savedVariables = questionsVariablesBackupRef.current;
        updatedQuestions = updatedQuestions.map((q: any) => {
          const savedQuestion = savedVariables.find((sq: any) => sq.questionId === q.id);
          if (savedQuestion) {
            return {
              ...q,
              responses: q.responses.map((r: any) => {
                const savedResponse = savedQuestion.responses.find(
                  (sr: any) => sr.responseId === r.id
                );
                return {
                  ...r,
                  variable: savedResponse?.variable,
                };
              }),
            };
          }
          return q;
        });
      }

      const currentQuestionsIds = test.questions.map((q: any) => q.id).filter(Boolean);

      updateTest({
        test: { ...test.test, diagram: value },
        questions: updatedQuestions,
      });
    } else {
      updateTest({
        test: { ...test.test, diagram: value },
      });
    }
  };

  const hasError = test?.errors?.title;

  // remove simple diagram on edit mode if restitution type is horizontal or vertical
  const options =
    test.isEdit &&
    (test?.test?.diagram?.id === 2 ||
      test?.test?.diagram?.id === 3 ||
      test?.test?.diagram?.id === 5)
      ? restitutionTypes?.filter((r: any) => r.id !== 1)
      : restitutionTypes;

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    questionsVariablesBackupRef.current = [];
  }, [id]);

  return (
    <>
      <div className={classNames(classes.marginTop, classes.fullWidth)}>
        <OaLabel>Titre</OaLabel>
        <TextField
          id="create-test-title"
          variant="outlined"
          className={classNames(classes.formInput, hasError && classes.inputError)}
          name="test-name"
          value={test.test.title}
          onChange={handleChange}
          placeholder="Renseignez le nom"
          ref={ref as any}
        />
      </div>

      <div className={classNames(classes.marginTop, classes.fullWidth)}>
        <OaLabel>Type de restitution</OaLabel>
        <Select
          styles={styles}
          components={{ DropdownIndicator: CustomDropdownIndicator }}
          placeholder="Sélectionnez"
          options={options as any}
          getOptionLabel={option => option.type}
          getOptionValue={option => option.id}
          value={test.test.diagram}
          onChange={handleSelectChange}
          className={classNames({
            [classes.categorySelect]: true,
            [classes.inputError]: false,
          })}
        />
      </div>

      <div className={classNames(classes.marginTop, classes.fullWidth)}>
        <OaLabel>Couleur du diagramme</OaLabel>
        <Select
          styles={styles}
          components={{ DropdownIndicator: CustomDropdownIndicator }}
          placeholder="Sélectionner"
          options={DIAGRAM_UNIVERS_OPTIONS}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          formatOptionLabel={formatUniversOptionLabel}
          value={getDiagramUniversOption(test.test.univers)}
          onChange={handleUniversChange}
          isClearable={true}
          className={classNames({
            [classes.categorySelect]: true,
            [classes.inputError]: false,
          })}
        />
      </div>
    </>
  );
});

export default TitlePart;
